import { forwardRef } from "react";

/**
 * ResponsiveImage
 * -----------------------------------------------------------------------------
 * A smart <picture> wrapper for luxury product imagery.
 *
 * • Unsplash URLs (https://images.unsplash.com/...) are transformed on the fly
 *   into multi-format, multi-size srcsets — Unsplash natively serves AVIF/WebP
 *   when we pass ?fm=avif or ?fm=webp so browsers pick the smallest supported
 *   format automatically via the <picture> element.
 * • Other URLs (Emergent object storage /api/files/..., data URIs, external
 *   hosts) are rendered as a plain <img> so nothing breaks.
 * • Zero visual change — object-fit / sizing / rounding all live on the outer
 *   className (applied to the <img>), and the wrapper <picture> is display:contents
 *   equivalent (no extra box).
 *
 * Props:
 *   src        original image URL
 *   alt        alt text (required for accessibility)
 *   sizes      responsive `sizes` attribute (e.g. "(max-width: 768px) 100vw, 60vw")
 *   priority   true → eager + fetchpriority=high  (use for the LCP image only)
 *   quality    JPEG/WebP/AVIF quality (default 78 — luxury needs premium quality)
 *   widths     explicit width breakpoints; defaults to [400,640,828,1200,1600,2000]
 *
 * All other props (className, onClick, data-testid, style, decoding, etc.) are
 * passed through to the underlying <img>.
 */
const DEFAULT_WIDTHS = [400, 640, 828, 1200, 1600, 2000];

const isUnsplash = (u) => typeof u === "string" && /^https?:\/\/images\.unsplash\.com\//.test(u);

const stripTransforms = (u) => {
  if (!u) return u;
  try {
    const url = new URL(u);
    // Remove the params we'll re-add
    ["w", "q", "fm", "auto", "fit", "crop"].forEach((k) => url.searchParams.delete(k));
    return url.toString();
  } catch {
    return u;
  }
};

const buildSrcSet = (src, fmt, widths, quality) => {
  const clean = stripTransforms(src);
  const sep = clean.includes("?") ? "&" : "?";
  return widths
    .map((w) => `${clean}${sep}fm=${fmt}&w=${w}&q=${quality}&fit=crop ${w}w`)
    .join(", ");
};

export const ResponsiveImage = forwardRef(function ResponsiveImage(
  {
    src,
    alt = "",
    sizes = "100vw",
    priority = false,
    quality = 78,
    widths = DEFAULT_WIDTHS,
    className,
    style,
    fallbackWidth = 1200,
    ...rest
  },
  ref
) {
  if (!src) return null;

  const commonImgProps = {
    ref,
    alt,
    className,
    style,
    sizes,
    decoding: priority ? "sync" : "async",
    loading: priority ? "eager" : "lazy",
    // React accepts lowercase fetchpriority; keep lowercase to match React 19.
    ...(priority ? { fetchpriority: "high" } : {}),
    ...rest,
  };

  // Non-Unsplash — plain img, no transforms possible.
  if (!isUnsplash(src)) {
    return <img src={src} {...commonImgProps} />;
  }

  const avifSet = buildSrcSet(src, "avif", widths, quality);
  const webpSet = buildSrcSet(src, "webp", widths, quality);
  const jpgSet = buildSrcSet(src, "jpg", widths, quality);
  const clean = stripTransforms(src);
  const sep = clean.includes("?") ? "&" : "?";
  const fallbackSrc = `${clean}${sep}fm=jpg&w=${fallbackWidth}&q=${quality}&fit=crop`;

  return (
    <picture>
      <source type="image/avif" srcSet={avifSet} sizes={sizes} />
      <source type="image/webp" srcSet={webpSet} sizes={sizes} />
      <img src={fallbackSrc} srcSet={jpgSet} {...commonImgProps} />
    </picture>
  );
});

/**
 * hiResSrc(src, width) — return a high-resolution single URL suitable for the
 * zoom / lightbox view. For Unsplash we request a large WebP variant; otherwise
 * we return the original URL unchanged.
 */
export const hiResSrc = (src, width = 2400, quality = 82) => {
  if (!isUnsplash(src)) return src;
  const clean = stripTransforms(src);
  const sep = clean.includes("?") ? "&" : "?";
  return `${clean}${sep}fm=webp&w=${width}&q=${quality}&fit=crop`;
};

export default ResponsiveImage;
